import { type Dispatch, type FC, type SetStateAction, useMemo, useState } from "react";
import { MessageStatus, type UserSentMessagesCollection } from "../../../utils/models";
import { type SystemChipItem, SystemChips } from "../../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { getFilteredThreads } from "./utils";

type FilterType = "messageStatus" | "readStatus";

type FilterChip = SystemChipItem<MessageStatus[]> & {
	filterType: FilterType;
};

type Props = {
	selectedFilterStatuses: MessageStatus[];
	setSelectedFilterStatuses: Dispatch<SetStateAction<MessageStatus[]>>;
	threads: UserSentMessagesCollection;
	unreadCounts?: Record<string, number>;
	showUnreadOnly: boolean;
	setShowUnreadOnly: Dispatch<SetStateAction<boolean>>;
};

const filterChips: FilterChip[] = [
	{
		label: "All",
		value: Object.values(MessageStatus),
		filterType: "messageStatus",
	},
	{
		label: "Failed",
		value: [MessageStatus.failed, MessageStatus.undelivered],
		filterType: "messageStatus",
	},
	{
		label: "In Progress",
		value: [MessageStatus.accepted, MessageStatus.queued, MessageStatus.sending],
		filterType: "messageStatus",
	},
	{
		label: "Unread",
		value: Object.values(MessageStatus),
		filterType: "readStatus",
	},
];

export const ChatThreadsFilterSelector: FC<Props> = ({
	setSelectedFilterStatuses,
	selectedFilterStatuses,
	threads,
	unreadCounts,
	showUnreadOnly,
	setShowUnreadOnly,
}) => {
	const [selectedItem, setSelectedItem] = useState<FilterChip>(() => {
		if (showUnreadOnly) {
			return filterChips.find((chip) => chip.filterType === "readStatus") ?? filterChips[0];
		}
		return (
			filterChips.find(
				(chip) =>
					chip.filterType === "messageStatus" &&
					chip.value.length === selectedFilterStatuses.length &&
					chip.value.every((status) => selectedFilterStatuses.includes(status)),
			) ?? filterChips[0]
		);
	});

	const filterByReadStatus = useMemo(() => {
		return Object.values(unreadCounts ?? {}).filter((count) => count > 0).length; // DEFAULT: data is absent only while summaries load.
	}, [unreadCounts]);

	const filtersAndAmounts = useMemo(() => {
		return filterChips.map((chip) => {
			if (chip.filterType === "readStatus") {
				return { ...chip, count: filterByReadStatus };
			}

			// Count by message status
			const filteredThreads = getFilteredThreads(chip.value, threads);
			const count = Object.entries(filteredThreads).map(([userId, messages]) => {
				const firstMessage = messages.length ? messages.reduce((max, msg) => (msg.dateCreated > max.dateCreated ? msg : max)) : undefined;
				return { userId, firstMessage };
			});
			return {
				...chip,
				count: chip.value.length === Object.values(MessageStatus).length ? count.length : count.filter((value) => value.firstMessage).length,
			};
		});
	}, [threads, filterByReadStatus]);

	return (
		<SystemChips
			items={filtersAndAmounts.map((chip) => ({
				value: chip.value,
				label: `${chip.label} (${chip.count})`,
			}))}
			variant={"outlined"}
			onChange={(value, _) => {
				const selectedChip = filterChips.find(
					(chip) => chip.value === value.value && chip.label === filtersAndAmounts.find((f) => f.value === value.value)?.label,
				);
				if (!selectedChip) return;

				if (selectedChip.filterType === "readStatus") {
					setShowUnreadOnly(true);
					setSelectedFilterStatuses(Object.values(MessageStatus));
				} else {
					setShowUnreadOnly(false);
					setSelectedFilterStatuses(selectedChip.value);
				}
				setSelectedItem(selectedChip);
			}}
			selectedItems={[selectedItem]}
		/>
	);
};
